import { addDisplayName, addHandlers, addProps, flowMax } from 'ad-hok'
import { FC } from 'react'
import { parseISO, setDate } from 'date-fns'
import { createUseStyles } from 'react-jss'
import { formatDateIso } from 'shared/src/utils/formatting'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { TextFieldNew } from './TextField'
import { Button } from './Button'

export const EARLIEST_DATE = new Date(2022, 0, 1)

export const getToday = () => new Date()

const formatDate = (date: Date) => {
  try {
    return formatDateIso(date)
  } catch {
    return undefined
  }
}

const useStyles = createUseStyles({
  buttons: {
    display: 'flex',
    gap: 10,
  },
})

interface Props {
  startDate: Date
  endDate: Date
  onChange: (startDate: Date, endDate: Date) => void
}

export const DateRange: FC<Props> = flowMax(
  addDisplayName('DateRange'),
  addHandlers({
    onChooseAllTime:
      ({ onChange: onChangeDateRange }) =>
      () => {
        onChangeDateRange(EARLIEST_DATE, getToday())
      },
    onChooseThisMonth:
      ({ onChange: onChangeDateRange }) =>
      () => {
        onChangeDateRange(setDate(new Date(), 1), getToday())
      },
    onChooseToday:
      ({ onChange: onChangeDateRange }) =>
      () => {
        onChangeDateRange(getToday(), getToday())
      },
    setStartDate:
      ({ endDate, onChange: onChangeDateRange }) =>
      (isoDate: string) => {
        onChangeDateRange(parseISO(isoDate), endDate)
      },
    setEndDate:
      ({ startDate, onChange: onChangeDateRange }) =>
      (isoDate: string) => {
        onChangeDateRange(startDate, parseISO(isoDate))
      },
  }),
  addProps(({ startDate, endDate }) => ({
    startDateIso: formatDate(startDate),
    endDateIso: formatDate(endDate),
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({
    startDateIso,
    endDateIso,
    setStartDate,
    setEndDate,
    onChooseAllTime,
    onChooseThisMonth,
    onChooseToday,
    classes,
    t,
  }) => (
    <div className={classes.buttons}>
      <TextFieldNew
        type="date"
        value={startDateIso}
        onChange={setStartDate}
        showClearInput={false}
      />
      <TextFieldNew
        type="date"
        value={endDateIso}
        onChange={setEndDate}
        showClearInput={false}
      />
      <Button
        text={t('admin.resources.general.allTime')}
        onClick={onChooseAllTime}
      />
      <Button
        text={t('admin.resources.general.thisMonth')}
        onClick={onChooseThisMonth}
      />
      <Button
        text={t('admin.resources.general.today')}
        onClick={onChooseToday}
      />
    </div>
  )
)
